import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Search,
  Filter,
  Check,
  Trash2,
  Edit3,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipWrapper } from "@/components/ui/TooltipWrapper";
import { toast } from "@/components/ui/sonner";
import {
  Product,
  Products,
  productsApi,
  ProfileClientAddProductDto,
  profileClientApi,
  commercialCyclesApi,
  CommercialCycle,
} from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  clientService,
} from "@/api/clientService";
import { formatCurrency } from "@/lib/formatters";
import { codeSeries, decodeSeries, digitalProducts, getMarca, mapEtapaToGradeName } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CatalogSkeleton } from "./components/CatalogSkeleton";
import { useCycleRequiredHandler } from "@/hooks/useCycleRequiredHandler";
import { isShipmentExemptionEnabledForCycle } from "@/features/shipmentExemptionFeature";
import { isShipmentExemptionCandidateDescription } from "@/lib/shipmentFreight";


/* ─── Tipos ─── */
interface ClientState {
  id: number;
  name: string;
  code?: string;
  city?: string;
  state?: string;
  studentCount?: number;
  shipping_fee?: number;
}

interface StagedProduct {
  valor: string;
  qtd: string;
  desconto: string;
  comentario: string;
  // Produto opcional para o cliente (profile_client.is_optional)
  opcional?: boolean;
  // Sem frete e sem remessa impressa (profile_client.is_shipment_exempt)
  semRemessa?: boolean;
}

interface Filters {
  marca: string;
  segmento: string;
  serie: string;
  useAluno: boolean;
  useProfessor: boolean;
  sku: string;
  cnx: string;
  tpMaterial: boolean;
  tpEbook: boolean;
  tpServico: boolean;
  entSemestral: boolean;
  entAnual: boolean;
  brandName: string;
  productTypeName: string;
  deliveryTypeName: string;
}

const defaultFilters: Filters = {
  marca: "",
  segmento: "",
  serie: "",
  useAluno: false,
  useProfessor: false,
  sku: "",
  cnx: "",
  tpMaterial: false,
  tpEbook: false,
  tpServico: false,
  entSemestral: false,
  entAnual: false,
  brandName: "",
  productTypeName: "",
  deliveryTypeName: "",
};

const CATALOG_PAGE_SIZE = 60;

/** Mapeamento dos status da API para labels em PT-BR. */
const CYCLE_STATUS_LABELS: Record<CommercialCycle["status"], string> = {
  Draft: "Rascunho",
  Published: "Publicado",
  Open: "Aberto",
  Closing: "Em fechamento",
  Closed: "Encerrado",
};



/* ─── Helpers de quantidade: zero é permitido, negativo/fracionado não (API valida >= 0) ─── */
const getQuantityError = (raw: string): string | null => {
  const value = (raw ?? "").trim();
  if (!value) return "Informe a quantidade.";
  const num = Number(value);
  if (!Number.isFinite(num)) return "Quantidade inválida.";
  if (num < 0) return "Quantidade não pode ser negativa.";
  if (!Number.isInteger(num)) return "Quantidade deve ser um número inteiro.";
  return null;
};

// Impede digitar sinal, expoente e separador decimal no input numérico de quantidade
const blockNonQuantityKeys = (e: KeyboardEvent<HTMLInputElement>) => {
  if (["-", "+", "e", "E", ".", ","].includes(e.key)) e.preventDefault();
};

/* ─── Helpers de máscara de moeda (fora do componente para evitar recriação por render) ─── */
const parseCurrencyInput = (masked: string): number => {
  const digits = masked.replace(/[^\d]/g, "");
  if (!digits) return 0;
  return Number(digits) / 100;
};

const formatCurrencyMask = (raw: string): string => {
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  const num = Number(digits) / 100;
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ─── Checkbox de flag do vínculo (profile_client.is_optional, profile_client.is_shipment_exempt) ─── */
// indeterminate = "não alterar" na edição em massa (mesma regra dos campos vazios)
type LinkFlagCheckboxProps = {
  id: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  style?: CSSProperties;
};

const LinkFlagCheckbox = ({ id, checked, indeterminate = false, onChange, style, label, tooltip, tooltipLabel }: LinkFlagCheckboxProps & {
  label: string;
  tooltip: string;
  tooltipLabel: string;
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, ...style }}>
    <input
      id={id}
      type="checkbox"
      checked={checked}
      ref={el => { if (el) el.indeterminate = indeterminate; }}
      onChange={e => onChange(e.target.checked)}
      style={{ width: 15, height: 15, accentColor: "#0d9488", cursor: "pointer" }}
    />
    <label htmlFor={id} style={{ fontSize: 12.5, fontWeight: 600, color: "#334155", cursor: "pointer" }}>
      {label}
    </label>
    <TooltipWrapper content={tooltip}>
      <button type="button" aria-label={tooltipLabel} style={{ display: "flex", padding: 0, border: "none", background: "none", color: "#64748b", cursor: "help" }}>
        <Info size={14} />
      </button>
    </TooltipWrapper>
  </div>
);

const OptionalProductCheckbox = (props: LinkFlagCheckboxProps) => (
  <LinkFlagCheckbox
    {...props}
    label="Produto opcional"
    tooltip="No pedido, entra na série com quantidade 0 e é editado separadamente dos demais produtos."
    tooltipLabel="O que é produto opcional?"
  />
);

// Exibido só para Avaliações e Simulados (ver canAddShipmentExemption/canEditShipmentExemption).
const ShipmentExemptCheckbox = (props: LinkFlagCheckboxProps) => (
  <LinkFlagCheckbox
    {...props}
    label="Sem frete e sem remessa impressa"
    tooltip="Avaliações e Simulados: o produto é faturado no pedido, mas não gera remessas impressas nem cobra o frete delas. Sem esta opção, o frete das remessas é cobrado no pedido."
    tooltipLabel="O que é sem frete e sem remessa impressa?"
  />
);

/* ─── Componente principal ─── */
export default function ManageProducts() {
  const navigate = useNavigate();
  const location = useLocation();
  const clientFromState = location.state?.client as ClientState | undefined;

  // Redirect back if no client data
  useEffect(() => {
    if (!clientFromState) {
      navigate("/backoffice/perfil-clientes", { replace: true });
    }
  }, [clientFromState, navigate]);

  const client = clientFromState!;

  const {
    selectedCycleYear,
    setSelectedCycleYear,
    handleCycleError,
  } = useCycleRequiredHandler();


  /* ─── Query: Ciclos comerciais (para o seletor manual) ─── */
  const { data: commercialCycles = [] } = useQuery<CommercialCycle[]>({
    queryKey: ["commercial-cycles"],
    queryFn: async () => {
      const response = await commercialCyclesApi.list();
      return response.data || [];
    },
  });

  /** Ciclos ativos (excluindo Fechados), ordenados do mais recente ao mais antigo. */
  const sortedCycles = useMemo(
    () =>
      [...commercialCycles]
        .sort((a, b) => b.year - a.year),
    [commercialCycles],
  );

  /**
   * Ao montar a página, sempre inicializa com o ciclo mais recente.
   * Usa um ref para garantir que o reset aconteça somente uma vez,
   * mesmo que sortedCycles carregue de forma assíncrona.
   */
  const cycleInitialized = useRef(false);
  useEffect(() => {
    if (sortedCycles.length > 0 && !cycleInitialized.current) {
      cycleInitialized.current = true;
      setSelectedCycleYear(sortedCycles[0].year);
    }
  }, [sortedCycles, setSelectedCycleYear]);


  /* ─── State: Filtros e busca ─── */
  const [quickSearch, setQuickSearch] = useState("");
  const debouncedSearch = useDebounce(quickSearch, 300);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(defaultFilters);

  /* ─── State: Seleção ─── */
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  /* ─── State: Expandido ─── */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  /* ─── State: Drawer Adicionar ─── */
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  // drawerReady separa a abertura do shell (imediata) da renderização do catálogo (próximo frame)
  const [drawerReady, setDrawerReady] = useState(false);

  useEffect(() => {
    if (showAddDrawer) {
      const id = requestAnimationFrame(() => setDrawerReady(true));
      return () => cancelAnimationFrame(id);
    } else {
      setDrawerReady(false);
      // Limpa seleção e campos ao fechar o drawer
      setStaged({});
      setStagedOrigins({});
      setBulkValor("");
      setBulkQty("");
      setBulkDesconto("");
      setBulkComentario("");
      setCatalogSearch("");
      setCatalogSerie("");
      setCatalogMarca("");
      setCatalogChipTipo("");
      setCatalogChipUso("");
      setCatalogLimit(CATALOG_PAGE_SIZE);
    }
  }, [showAddDrawer]);

  const [catalogSearch, setCatalogSearch] = useState("");
  const debouncedCatalogSearch = useDebounce(catalogSearch, 300);
  const [catalogSerie, setCatalogSerie] = useState("");
  const [catalogMarca, setCatalogMarca] = useState("");
  const [catalogChipTipo, setCatalogChipTipo] = useState("");
  const [catalogChipUso, setCatalogChipUso] = useState("");

  /* Limite de itens renderizados — reset ao mudar filtros */
  const [catalogLimit, setCatalogLimit] = useState(CATALOG_PAGE_SIZE);
  useEffect(() => {
    setCatalogLimit(CATALOG_PAGE_SIZE);
  }, [debouncedCatalogSearch, catalogChipTipo, catalogChipUso, catalogSerie, catalogMarca]);
  const [staged, setStaged] = useState<Record<number, StagedProduct>>({});
  const [stagedOrigins, setStagedOrigins] = useState<Record<number, boolean>>({});
  const [bulkValor, setBulkValor] = useState("");
  const [bulkQty, setBulkQty] = useState("");
  const [bulkDesconto, setBulkDesconto] = useState("");
  const [bulkComentario, setBulkComentario] = useState("");
  // null (checkbox indeterminado) mantém o valor de cada item; true/false marca todos como opcional/obrigatório
  const [bulkOpcional, setBulkOpcional] = useState<boolean | null>(null);
  // null (checkbox indeterminado) mantém o valor de cada item
  const [bulkSemRemessa, setBulkSemRemessa] = useState<boolean | null>(null);

  /* ─── State: Drawer Editar ─── */
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editStaged, setEditStaged] = useState<Record<number, StagedProduct>>({});
  const [editBulkValor, setEditBulkValor] = useState("");
  const [editBulkQty, setEditBulkQty] = useState("");
  const [editBulkDesconto, setEditBulkDesconto] = useState("");
  const [editBulkComentario, setEditBulkComentario] = useState("");
  // null (checkbox indeterminado) mantém o valor de cada item; true/false marca todos como opcional/obrigatório
  const [editBulkOpcional, setEditBulkOpcional] = useState<boolean | null>(null);
  const [editBulkSemRemessa, setEditBulkSemRemessa] = useState<boolean | null>(null);

  /* ─── State: Modal Confirmar Inativação ─── */
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"bulk" | number | null>(null);
  const [confirmComentario, setConfirmComentario] = useState("");

  /* ─── State: Loading de ação ─── */
  const [actionLoading, setActionLoading] = useState(false);

  /* ─── React Query ─── */
  const queryClient = useQueryClient();

  /* ─── Query: Produtos vinculados ─── */
  const {
    data: products = [],
    isLoading: loadingProducts,
  } = useQuery<Products[]>({
    queryKey: ["clientProducts", client?.id, selectedCycleYear],
    queryFn: async () => {
      try {
        const { data } = await profileClientApi.getProfileDetails(
          client.id,
          selectedCycleYear ?? undefined,
        );
        if (!Array.isArray(data)) return [];
        // A API retorna agrupado por materialUseName; achata para uma lista plana
        return data.flatMap((group) =>
          group.products.map((p) => ({
            ...p,
            materialUseName: group.materialUseName,
          }))
        );
      } catch (error) {
        if (handleCycleError(error)) return [];
        throw error;
      }
    },
    enabled: !!client?.id,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true
  });

  /* ─── Query: Catálogo ─── */
  const {
    data: catalogProducts = [],
    isLoading: loadingCatalog,
  } = useQuery<Product[]>({
    queryKey: ["catalogProducts", selectedCycleYear],
    queryFn: async () => {
      const { data } = await productsApi.getActive(selectedCycleYear ?? undefined);
      return (data as any) || [];
    },
    staleTime: 5 * 60 * 1000, // cache válido por 5 min — não re-busca se já tiver dados frescos
  });

  /* ─── Produtos ativos ─── */
  const activeProducts = useMemo(
    () => products.filter((p: any) => p.isActive),
    [products]
  );

  /* ─── Flag "sem frete e sem remessa impressa" ─── */
  // Só para Avaliações e Simulados (descrição começando com "Avaliaç…"/"Simulad…"). Na edição também aparece onde
  // a API indica que o flag tem efeito (shipmentExemptionApplies), mesmo com outra descrição.
  const isShipmentExemptionVisible = isShipmentExemptionEnabledForCycle(selectedCycleYear);
  const canAddShipmentExemption = (productName: string) =>
    isShipmentExemptionVisible && isShipmentExemptionCandidateDescription(productName);
  const canEditShipmentExemption = (p?: Products) =>
    isShipmentExemptionVisible
    && (!!p?.shipmentExemptionApplies || isShipmentExemptionCandidateDescription(p?.productDescription));
  const hasEditableShipmentExemption = Object.keys(editStaged).some(idStr =>
    canEditShipmentExemption(activeProducts.find(product => product.profileClientId === Number(idStr))));

  /* ─── Existing product IDs ─── */
  const existingProductIds = useMemo(
    () => activeProducts.map((p: any) => p.productId),
    [activeProducts]
  );

  const existingProductDeactivate = useMemo(
    () => products.filter((p: any) => !p.isActive).map((p: any) => p.productId),
    [products]
  );

  /* ─── Filtragem de produtos vinculados ─── */
  const filteredProducts = useMemo(() => {
    let result = activeProducts;

    // Busca rápida
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      result = result.filter((p: any) =>
        p.productDescription?.toLowerCase().includes(term) ||
        p.productSku?.toLowerCase().includes(term) ||
        p.productCode?.toLowerCase().includes(term) ||
        p.materialUseName?.toLowerCase().includes(term) ||
        p.segmentName?.toLowerCase().includes(term) ||
        p.seriesName?.toLowerCase().includes(term) ||
        p.brandName?.toLowerCase().includes(term)
      );
    }

    // Filtros aplicados
    if (filtersApplied) {
      const f = appliedFilters;

      if (f.segmento) {
        result = result.filter((p: any) => p.segmentName === f.segmento);
      }
      if (f.serie) {
        result = result.filter((p: any) => p.seriesName === f.serie);
      }
      if (f.useAluno && !f.useProfessor) {
        result = result.filter((p: any) => p.materialUseName?.toLowerCase().includes("aluno"));
      }
      if (f.useProfessor && !f.useAluno) {
        result = result.filter((p: any) => p.materialUseName?.toLowerCase().includes("professor"));
      }
      if (f.sku) {
        result = result.filter((p: any) => (p.productSku || "").includes(f.sku));
      }
      if (f.brandName) {
        result = result.filter((p: any) => p.brandName === f.brandName);
      }
      if (f.productTypeName) {
        result = result.filter((p: any) => p.productTypeName === f.productTypeName);
      }
      if (f.deliveryTypeName) {
        result = result.filter((p: any) => p.deliveryTypeName === f.deliveryTypeName);
      }
    }

    return result;
  }, [activeProducts, debouncedSearch, filtersApplied, appliedFilters]);

  /* ─── ACV e contadores ─── */
  const linkedCount = activeProducts.length;
  const acvTotal = useMemo(
    () => activeProducts.reduce((sum, p) => sum + ((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0)), 0),
    [activeProducts]
  );

  /* ─── Seleção ─── */
  const editableFilteredProducts = filteredProducts;
  const selectedCount = Object.keys(selected).length;
  const allSelected = editableFilteredProducts.length > 0 && editableFilteredProducts.every(p => selected[p.profileClientId]);

  const toggleSelect = (product: Products) => {
    setSelected(prev => {
      const next = { ...prev };

      if (next[product.profileClientId]) {
        delete next[product.profileClientId];
      } else {
        next[product.profileClientId] = true;
      }

      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next: Record<number, boolean> = {};
      editableFilteredProducts.forEach(p => { next[p.profileClientId] = true; });
      setSelected(next);
    }
  };

  /* ─── Expandir variantes (agrupado por productDescription) ─── */
  const toggleExpand = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /* ─── Filtros ─── */
  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setFiltersApplied(true);
  };
  const clearFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setFiltersApplied(false);
  };

  /* ─── Opções de filtro dinâmicas ─── */
  const segmentoOptions = useMemo(
    () => [...new Set(activeProducts.map(p => p.segmentName).sort((a, b) => codeSeries(a) - codeSeries(b)).filter(Boolean))],
    [activeProducts]
  );
  const serieOptions = useMemo(
    () => [...new Set(activeProducts.map(p => p.seriesName).sort((a, b) => codeSeries(a) - codeSeries(b)).filter(Boolean))],
    [activeProducts]
  );
  const brandNameOptions = useMemo(
    () => [...new Set(activeProducts.map(p => p.brandName).filter(Boolean))] as string[],
    [activeProducts]
  );
  const productTypeNameOptions = useMemo(
    () => [...new Set(activeProducts.map(p => p.productTypeName).filter(Boolean))] as string[],
    [activeProducts]
  );
  const deliveryTypeNameOptions = useMemo(
    () => [...new Set(activeProducts.map(p => p.deliveryTypeName).filter(Boolean))] as string[],
    [activeProducts]
  );

  /* ─── Catálogo filtrado (drawer adicionar) ─── */
  const filteredCatalog = useMemo(() => {
    return catalogProducts
      .filter(e => !digitalProducts.includes(e.sku))
      .filter(p => !existingProductIds.includes(p.id))
      .filter(p => {
        if (debouncedCatalogSearch) {
          const term = debouncedCatalogSearch.toLowerCase();
          return (
            (p as any).descricaoProduto?.toLowerCase().includes(term) ||
            p.descricao_produto?.toLowerCase().includes(term) ||
            p.sku?.toLowerCase().includes(term) ||
            p.codigoProduto?.toLowerCase().includes(term) ||
            p.codigo_produto?.toLowerCase().includes(term) ||
            p.segmento?.toLowerCase().includes(term) ||
            mapEtapaToGradeName(p.serie || "").toLowerCase().includes(term)
          );
        }
        return true;
      })
      .filter(p => {
        if (catalogChipTipo) {
          // Map tipo to product field
          const productTypeMap: Record<string, number> = {
            "Material": 1,
            "Ebook": 2,
            "Servico": 3,
            "Escola": 4,
          }
          if (productTypeMap[catalogChipTipo] !== (p as any).deliveryTypeId) return false;
        }
        if (catalogChipUso && p.utilizacao) {
          if (p.utilizacao.toLowerCase() !== catalogChipUso.toLowerCase()) return false;
        }
        if (catalogSerie && p.serie) {
          if (mapEtapaToGradeName(p.serie) !== catalogSerie) return false;
        }
        if (catalogMarca && p.marca) {
          if (String(p.marca) !== catalogMarca) return false;
        }
        return true;
      });
  }, [catalogProducts, existingProductIds, debouncedCatalogSearch, catalogChipTipo, catalogChipUso, catalogSerie, catalogMarca]);

  const catalogSerieOptions = useMemo(
    () => [
      ...new Set(
        catalogProducts
          .sort((a, b) => Number(a.serie) - Number(b.serie))
          .map(p => mapEtapaToGradeName(p.serie || ""))
          .filter(Boolean)
      )
    ],
    [catalogProducts]
  );
  const catalogMarcaOptions = useMemo(
    () => [...new Set(catalogProducts.map(p => p.marca).filter(Boolean))],
    [catalogProducts]
  );


  const handleValorChange = (id: number, rawInput: string) => {
    const formatted = formatCurrencyMask(rawInput);
    updateStagedField(id, "valor", formatted);
  };

  /* ─── Staged rows (drawer adicionar) ─── */
  const stagedRows = useMemo(() => {
    return Object.entries(staged).map(([idStr, st]) => {
      const id = Number(idStr);
      const p = catalogProducts.find(c => c.id === id);
      const valorNum = parseCurrencyInput(st.valor);
      const qtdNum = Number(st.qtd) || 0;
      const descNum = Number(st.desconto) || 0;
      const total = valorNum * qtdNum;
      const missingValor = valorNum < 0;
      const qtdError = getQuantityError(st.qtd);
      const missingQtd = qtdError !== null;
      const comentarioLen = (st.comentario || "").trim().length;
      const missingComentario = comentarioLen < 5;
      return {
        id,
        name: p?.descricao_produto || (p as any)?.descricaoProduto || "",
        sku: p?.sku || "",
        productCode: (p as any)?.codigoProduto || (p as any)?.codigo_produto || "",
        segmento: p?.segmento || "",
        serie: p?.serie ? mapEtapaToGradeName(p.serie) : "",
        useType: p?.utilizacao || "",
        valor: st.valor,
        qtd: st.qtd,
        desconto: st.desconto,
        comentario: st.comentario,
        opcional: !!st.opcional,
        semRemessa: !!st.semRemessa,
        charsRemaining: 250 - comentarioLen,
        totalStr: formatCurrency(total),
        missingValor,
        missingQtd,
        qtdError,
        missingComentario,
        product: p,
      };
    });
  }, [staged, catalogProducts]);

  const stagedGrandTotal = useMemo(() => {
    return stagedRows.reduce((sum, r) => {
      const v = parseCurrencyInput(r.valor);
      const q = Number(r.qtd) || 0;
      return sum + v * q;
    }, 0);
  }, [stagedRows]);

  const addDisabled = stagedRows.length === 0 || stagedRows.some(r => r.missingValor || r.missingQtd || r.missingComentario);

  const editSaveDisabled = Object.keys(editStaged).length === 0
    || Object.values(editStaged).some(d => (d.comentario || "").trim().length < 5 || getQuantityError(d.qtd) !== null);

  /* ─── Ações: Staging ─── */
  const catalogProductsById = useMemo(
    () => new Map(catalogProducts.map(product => [product.id, product])),
    [catalogProducts]
  );

  // Exclusivo deste drawer: permite iniciar por qualquer item e percorrer toda
  // a corrente, sem alterar a direção de relatedProductIds para outros fluxos.
  const catalogRelationshipGraph = useMemo(() => {
    const graph = new Map<number, Set<number>>();
    catalogProducts.forEach(product => graph.set(product.id, new Set()));

    catalogProducts.forEach(product => {
      (product.relatedProductIds ?? []).forEach(relatedProductId => {
        if (!graph.has(relatedProductId)) return;
        graph.get(product.id)?.add(relatedProductId);
        graph.get(relatedProductId)?.add(product.id);
      });
    });

    return graph;
  }, [catalogProducts]);

  const getSelectableProductIds = (originProductId: number) => {
    if (!catalogRelationshipGraph.has(originProductId)) return [];

    const connectedProductIds = new Set<number>([originProductId]);
    const pendingProductIds = [originProductId];

    while (pendingProductIds.length > 0) {
      const currentProductId = pendingProductIds.shift()!;
      catalogRelationshipGraph.get(currentProductId)?.forEach(relatedProductId => {
        if (connectedProductIds.has(relatedProductId)) return;
        connectedProductIds.add(relatedProductId);
        pendingProductIds.push(relatedProductId);
      });
    }

    return [...connectedProductIds]
      .filter(productId => !existingProductIds.includes(productId));
  };

  const createInitialStagedProduct = (productId: number): StagedProduct => {
    const product = catalogProductsById.get(productId);
    const precoFormatado = (product?.preco || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return {
      valor: precoFormatado,
      qtd: "1",
      desconto: "",
      comentario: "",
      opcional: false,
      semRemessa: false,
    };
  };

  const isAutomaticallyRelatedProduct = (productId: number) =>
    !!staged[productId] && !stagedOrigins[productId];

  const toggleStage = (productId: number) => {
    if (staged[productId]) {
      unstageProduct(productId);
      return;
    }

    const productIdsToAdd = getSelectableProductIds(productId);
    setStaged(previous => {
      const next = { ...previous };
      productIdsToAdd.forEach(id => {
        next[id] = previous[id] ?? createInitialStagedProduct(id);
      });
      return next;
    });
    setStagedOrigins(previous => ({ ...previous, [productId]: true }));
  };

  const stageAllFiltered = () => {
    const filteredProductIds = filteredCatalog.map(product => product.id);
    const productIdsToAdd = new Set(
      filteredProductIds.flatMap(productId => getSelectableProductIds(productId))
    );

    setStaged(previous => {
      const next = { ...previous };
      productIdsToAdd.forEach(id => {
        next[id] = previous[id] ?? createInitialStagedProduct(id);
      });
      return next;
    });
    setStagedOrigins(previous => {
      const next = { ...previous };
      filteredProductIds.forEach(id => { next[id] = true; });
      return next;
    });
  };

  const unstageProduct = (id: number) => {
    setStaged(previous => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
    setStagedOrigins(previous => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
  };

  const updateStagedField = (id: number, key: keyof StagedProduct, value: string) => {
    setStaged(prev => ({
      ...prev,
      [id]: { ...prev[id], [key]: value },
    }));
  };

  const applyBulkAll = () => {
    if (!bulkValor && !bulkQty && !bulkDesconto && !bulkComentario && bulkOpcional === null && bulkSemRemessa === null) return;
    setStaged(prev => {
      const next: Record<number, StagedProduct> = {};
      Object.entries(prev).forEach(([id, cur]) => {
        next[Number(id)] = {
          valor: bulkValor || cur.valor,
          qtd: bulkQty || cur.qtd,
          desconto: bulkDesconto || cur.desconto,
          comentario: bulkComentario || cur.comentario,
          opcional: bulkOpcional ?? cur.opcional,
          semRemessa: bulkSemRemessa ?? cur.semRemessa,
        };
      });
      return next;
    });
  };

  /* ─── Ação: Adicionar produtos ─── */
  const handleConfirmAddProducts = async () => {
    setActionLoading(true);
    try {
      for (const row of stagedRows) {
        if (row.missingValor || row.missingQtd || row.missingComentario) continue;
        const product = row.product;
        if (!product) continue;

        const qtd = Number(row.qtd);
        const desc = Number(row.desconto) || 0;
        const comentario = row.comentario.trim();
        const valorNum = parseCurrencyInput(row.valor);

        const dataDTO: ProfileClientAddProductDto = {
          clientId: client.id,
          productId: product.id,
          productStatus: true,
          contractualQuantity: qtd,
          discount: desc,
          content: comentario,
          productPrice: valorNum,
          priceReferenceYear: selectedCycleYear ?? undefined,
          // Só envia quando marcado; sem o campo, o vínculo novo entra como obrigatório
          ...(row.opcional ? { isOptional: true } : {}),
          // Idem: sem o campo, o vínculo novo gera remessas normalmente
          ...(canAddShipmentExemption(row.name) && row.semRemessa ? { isShipmentExempt: true } : {}),
        };

        const response = await profileClientApi.addProductProfileClient(dataDTO);
        if (!response) throw new Error("Erro ao salvar produto!");
      }

      // Rebusca os produtos na API para refletir o estado real do servidor
      await queryClient.invalidateQueries({
        queryKey: ["clientProducts", client.id, selectedCycleYear],
      });

      toast.success("Produtos adicionados com sucesso!", { position: "top-center" });
      setStaged({});
      setStagedOrigins({});
      setBulkValor("");
      setBulkQty("");
      setBulkDesconto("");
      setBulkComentario("");
      setBulkOpcional(null);
      setBulkSemRemessa(null);
      setShowAddDrawer(false);
    } catch (error: any) {
      console.error("Erro ao adicionar produtos:", error);
      toast.error("Erro ao inativar produto(s)", {
        description:
          error?.response?.data?.message ||
          error.message ||
          "Tente novamente.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── Ação: Remover produto individual ─── */
  const handleRemoveSingle = (profileClientId: number) => {
    setConfirmMode(profileClientId);
    setConfirmComentario("");
    setShowConfirm(true);
  };

  /* ─── Ação: Remover em massa ─── */
  const handleBulkRemove = () => {
    setConfirmMode("bulk");
    setConfirmComentario("");
    setShowConfirm(true);
  };

  /* ─── Ação: Confirmar inativação ─── */
  const handleConfirmInactivate = async () => {
    if (!confirmComentario.trim()) return;
    setActionLoading(true);

    try {
      const idsToRemove: number[] = [];
      if (confirmMode === "bulk") {
        idsToRemove.push(...Object.keys(selected).map(Number));
      } else if (typeof confirmMode === "number") {
        idsToRemove.push(confirmMode);
      }

      for (const id of idsToRemove) {
        await profileClientApi.profileClientDeactivate(String(id), { content: confirmComentario });
      }

      // Rebusca os produtos na API para refletir o estado real do servidor
      await queryClient.invalidateQueries({
        queryKey: ["clientProducts", client.id, selectedCycleYear],
      });

      setSelected({});
      setShowConfirm(false);
      setConfirmMode(null);
      setConfirmComentario("");
      toast.success("Produto(s) inativado(s) com sucesso!");
    } catch (error: any) {
      console.error("Erro ao inativar:", error);
      toast.error("Erro ao inativar produto(s)", {
        description:
          error?.response?.data?.message ||
          error.message ||
          "Tente novamente.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  /* ─── Ação: Abrir drawer editar ─── */
  const openEditDrawer = () => {
    const es: Record<number, StagedProduct> = {};
    Object.keys(selected).forEach(idStr => {
      const id = Number(idStr);
      const p = activeProducts.find(p => p.profileClientId === id);
      if (p) {
        const precoFormatado = (p.priceWithoutDiscount ?? p.productPrice ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        es[id] = {
          valor: precoFormatado,
          qtd: String(p.contractualQuantity ?? 1),
          desconto: String(0),
          comentario: "",
          opcional: !!p.isOptional,
          semRemessa: !!p.isShipmentExempt,
        };
      }
    });
    setEditStaged(es);
    setEditBulkOpcional(null);
    setEditBulkSemRemessa(null);
    setShowEditDrawer(true);
  };

  /* ─── Ação: Salvar edições ─── */
  const handleSaveEdits = async () => {
    if (editSaveDisabled) return;
    setActionLoading(true);
    try {
      for (const [idStr, data] of Object.entries(editStaged)) {
        const id = Number(idStr);
        if (getQuantityError(data.qtd) !== null) continue;
        const qtd = Number(data.qtd);
        const desc = Number(data.desconto) || 0;
        const comentario = data.comentario.trim();
        const valorNum = parseCurrencyInput(data.valor);

        const product = activeProducts.find(p => p.profileClientId === id);
        if (!product) continue;

        // Só envia a flag quando o item foi alterado; sem o campo, o backend mantém o valor atual
        const optionalChanged = data.opcional !== undefined && data.opcional !== !!product.isOptional;
        const shipmentExemptChanged = canEditShipmentExemption(product)
          && data.semRemessa !== undefined && data.semRemessa !== !!product.isShipmentExempt;

        await clientService.updateClientProduct(id, {
          contractualQuantity: qtd,
          discount: desc,
          adjustment: desc,
          content: comentario,
          productId: product.productId,
          productPrice: valorNum,
          ...(optionalChanged ? { isOptional: data.opcional } : {}),
          ...(shipmentExemptChanged ? { isShipmentExempt: data.semRemessa } : {}),
        });
      }

      // Rebusca os produtos na API para refletir o estado real do servidor
      await queryClient.invalidateQueries({
        queryKey: ["clientProducts", client.id, selectedCycleYear],
      });

      toast.success("Alterações salvas com sucesso!");
      setShowEditDrawer(false);
      setEditStaged({});
      setSelected({});
    } catch (error: any) {
      console.error("Erro ao salvar edições:", error);
      toast.error("Erro ao inativar produto(s)", {
        description:
          error?.response?.data?.message ||
          error.message ||
          "Tente novamente.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const applyEditBulkAll = () => {
    if (!editBulkValor && !editBulkQty && !editBulkDesconto && !editBulkComentario && editBulkOpcional === null && editBulkSemRemessa === null) return;
    setEditStaged(prev => {
      const next: Record<number, StagedProduct> = {};
      Object.entries(prev).forEach(([id, cur]) => {
        next[Number(id)] = {
          valor: editBulkValor || cur.valor,
          qtd: editBulkQty || cur.qtd,
          desconto: editBulkDesconto || cur.desconto,
          comentario: editBulkComentario || cur.comentario,
          opcional: editBulkOpcional ?? cur.opcional,
          semRemessa: editBulkSemRemessa ?? cur.semRemessa,
        };
      });
      return next;
    });
  };

  /* ─── Confirm label ─── */
  const confirmCountLabel = useMemo(() => {
    if (confirmMode === "bulk") return `${selectedCount} produto(s)`;
    if (typeof confirmMode === "number") {
      const p = activeProducts.find(p => p.profileClientId === confirmMode);
      return p ? `"${p.productDescription}"` : "produto";
    }
    return "";
  }, [confirmMode, selectedCount, activeProducts]);

  if (!clientFromState) return null;

  /* ─────────────────────────────────────────────── */
  /*                     RENDER                      */
  /* ─────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: "100vh", background: "#f7f8fa", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif", color: "#1f2933" }}>

      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "28px 40px 140px" }}>

        {/* Voltar */}
        <button
          onClick={() => navigate(-1)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b", background: "none", border: "none", cursor: "pointer", marginBottom: 20, padding: 0 }}
        >
          <ArrowLeft size={15} /> Voltar para Perfis de Cliente
        </button>

        {/* Título */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Package size={22} color="#4338ca" />
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>Gerenciar Produtos</div>
            <div style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Adicione, edite ou remova produtos vinculados ao perfil do cliente.</div>
          </div>
        </div>

        {/* Card do cliente */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "18px 22px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 9, background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Package size={18} color="#2563eb" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{client.name}</span>
                {client.code && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 9px" }}>{client.code}</span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: "#94a3b8", marginTop: 2 }}>
                {client.city && client.state ? `${client.city}, ${client.state}` : ""}{client.studentCount ? ` · ${client.studentCount} alunos` : ""}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 4 }}>Ciclo comercial</div>
              <Select
                value={selectedCycleYear ? String(selectedCycleYear) : ""}
                onValueChange={(v) => setSelectedCycleYear(Number(v))}
              >
                <SelectTrigger style={{ width: 160, height: 34, fontSize: 13 }}>
                  <SelectValue placeholder="Selecione o ciclo" />
                </SelectTrigger>
                <SelectContent>
                  {sortedCycles.map((c) => (
                    <SelectItem key={c.year} value={String(c.year)}>
                      Ciclo {c.year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11.5, color: "#94a3b8" }}>Produtos vinculados</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>{linkedCount}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11.5, color: "#94a3b8" }}>ACV Total</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#0d9488" }}>{formatCurrency(acvTotal)}</div>
            </div>
          </div>
        </div>

        {/* Busca rápida */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "6px 6px 6px 16px", display: "flex", alignItems: "center", gap: 10, marginBottom: 20, boxShadow: "0 1px 2px rgba(0,0,0,.02)" }}>
          <Search size={18} color="#94a3b8" />
          <input
            type="text"
            placeholder="Busca rápida — pesquise por nome, SKU, código do produto, CNX, marca, segmento ou série..."
            style={{ flex: 1, border: "none", fontSize: 14.5, padding: "12px 0", background: "transparent", color: "#1f2933", outline: "none" }}
            value={quickSearch}
            onChange={e => setQuickSearch(e.target.value)}
          />
          {quickSearch && (
            <button onClick={() => setQuickSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
              <X size={16} color="#94a3b8" />
            </button>
          )}
        </div>

        {/* Grid principal: Filtros + Lista */}
        <div style={{ display: "grid", gridTemplateColumns: "272px 1fr", gap: 24, alignItems: "flex-start" }}>

          {/* Sidebar de Filtros */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, position: "sticky", top: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "#1e293b", display: "flex", alignItems: "center", gap: 7 }}>
                <Filter size={15} color="#0d9488" />
                Filtros
              </div>
              <button onClick={clearFilters} style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>Limpar</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Segmento */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Segmento</label>
                <select value={filters.segmento} onChange={e => setFilters(f => ({ ...f, segmento: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933", background: "#fff" }}>
                  <option value="">Todos</option>
                  {segmentoOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              {/* Série */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Série</label>
                <select value={filters.serie} onChange={e => setFilters(f => ({ ...f, serie: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933", background: "#fff" }}>
                  <option value="">Todas</option>
                  {serieOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              {/* Tipo de Uso */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 8 }}>Tipo de Uso</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "#334155", cursor: "pointer" }}>
                    <input type="checkbox" checked={filters.useAluno} onChange={() => setFilters(f => ({ ...f, useAluno: !f.useAluno }))} style={{ width: 15, height: 15, accentColor: "#0d9488" }} />
                    Aluno
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "#334155", cursor: "pointer" }}>
                    <input type="checkbox" checked={filters.useProfessor} onChange={() => setFilters(f => ({ ...f, useProfessor: !f.useProfessor }))} style={{ width: 15, height: 15, accentColor: "#0d9488" }} />
                    Professor
                  </label>
                </div>
              </div>

              {/* Marca */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Marca</label>
                <select value={filters.brandName} onChange={e => setFilters(f => ({ ...f, brandName: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933", background: "#fff" }}>
                  <option value="">Todas</option>
                  {brandNameOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              {/* Tipo do Produto */}
              {/*<div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Tipo do Produto</label>
                <select value={filters.productTypeName} onChange={e => setFilters(f => ({ ...f, productTypeName: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933", background: "#fff" }}>
                  <option value="">Todos</option>
                  {productTypeNameOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>*/}

              {/* Tipo de Entrega */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Tipo de Entrega</label>
                <select value={filters.deliveryTypeName} onChange={e => setFilters(f => ({ ...f, deliveryTypeName: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933", background: "#fff" }}>
                  <option value="">Todos</option>
                  {deliveryTypeNameOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>

              {/* SKU */}
              {/* <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>SKU</label>
                <input type="text" placeholder="Ex: 4126001010018" value={filters.sku} onChange={e => setFilters(f => ({ ...f, sku: e.target.value }))} style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, color: "#1f2933" }} />
              </div> */}

              {/* Aplicar */}
              <button onClick={applyFilters} style={{ width: "100%", background: "#0d9488", color: "#fff", border: "none", borderRadius: 8, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                Aplicar filtros
              </button>

              {filtersApplied && (
                <div style={{ fontSize: 11.5, color: "#0d9488", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 7, padding: "8px 10px", textAlign: "center" }}>
                  Filtros aplicados
                </div>
              )}
            </div>
          </div>

          {/* Lista de produtos */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569", cursor: "pointer" }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} style={{ width: 16, height: 16, accentColor: "#0d9488" }} />
                  Selecionar todos
                </label>
                <span style={{ fontSize: 13, color: "#94a3b8" }}>·</span>
                <span style={{ fontSize: 13, color: "#64748b" }}>{filteredProducts.length} produtos vinculados</span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0d9488", background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 20, padding: "5px 12px" }}>{linkedCount} vinculados</span>
                <button onClick={() => setShowAddDrawer(true)} style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <Plus size={14} /> Adicionar Produtos
                </button>
              </div>
            </div>

            {loadingProducts ? (
              <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>Carregando produtos...</div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb" }}>
                <Package size={40} color="#cbd5e1" style={{ margin: "0 auto 12px" }} />
                <div style={{ fontSize: 15, fontWeight: 600, color: "#64748b" }}>Nenhum produto encontrado</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Tente ajustar seus termos de busca ou filtros.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {filteredProducts.map((p: any) => {
                  const isSelected = !!selected[p.profileClientId];
                  const borderColor = isSelected ? "#0d9488" : "#e5e7eb";
                  let iconBg = "#f1f5f9", iconColor = "#64748b";
                  if (p.materialUseName?.toLowerCase().includes("aluno")) { iconBg = "#eff6ff"; iconColor = "#2563eb"; }
                  if (p.materialUseName?.toLowerCase().includes("professor")) { iconBg = "#f5f3ff"; iconColor = "#7c3aed"; }

                  const totalValue = (p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity || 0);

                  return (
                    <div key={p.profileClientId} style={{ background: "#fff", border: `1px solid ${borderColor}`, borderRadius: 12, padding: "16px 18px", display: "flex", gap: 14, transition: "border-color 0.15s" }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(p)}
                        style={{
                          width: 17,
                          height: 17,
                          accentColor: "#0d9488",
                          marginTop: 3,
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                      />

                      <div style={{ width: 34, height: 34, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Package size={16} color={iconColor} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 14.5, fontWeight: 700, color: "#1e293b" }}>{p.productDescription}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {p.isOptional && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 20, padding: "4px 10px" }}>
                                Opcional
                              </span>
                            )}
                            {canEditShipmentExemption(p) && p.isShipmentExempt && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: 20, padding: "4px 10px" }}>
                                Sem remessa
                              </span>
                            )}
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#0d9488", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 20, padding: "4px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                              <Check size={11} /> Vinculado
                            </span>
                            <button
                              onClick={() => handleRemoveSingle(p.profileClientId)}
                              title="Remover"
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: 8,
                                border: "1px solid #fecaca",
                                background: "#fef2f2",
                                color: "#dc2626",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                          {p.productSku && (
                            <span style={{ fontSize: 11, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20, padding: "3px 10px" }}>SKU: {p.productSku}</span>
                          )}
                          {p.productCode && (
                            <span style={{ fontSize: 11, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20, padding: "3px 10px" }}>Código: {p.productCode}</span>
                          )}
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#334155", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 20, padding: "3px 10px" }}>{p.materialUseName}</span>
                          {p.segmentName && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#fff", background: "#0d9488", borderRadius: 20, padding: "3px 10px" }}>{p.segmentName}</span>
                          )}
                          {p.seriesName && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", borderRadius: 20, padding: "3px 10px" }}>{p.seriesName}</span>
                          )}
                          {p.brandName && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 20, padding: "3px 10px" }}>{p.brandName}</span>
                          )}
                          {p.productTypeName && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#7e22ce", background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 20, padding: "3px 10px" }}>{p.productTypeName}</span>
                          )}
                          {p.deliveryTypeName && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#be185d", background: "#fdf2f8", border: "1px solid #fbcfe8", borderRadius: 20, padding: "3px 10px" }}>{p.deliveryTypeName}</span>
                          )}
                        </div>

                        <div style={{ marginTop: 8, fontSize: 12.5, color: "#64748b" }}>
                          Qtd. Ref: {p.contractualQuantity} &nbsp;·&nbsp;
                          Unit.: <strong style={{ color: "#1e293b" }}>{formatCurrency(p.priceWithoutDiscount ?? p.productPrice ?? 0)}</strong> &nbsp;·&nbsp;
                          Total: <strong style={{ color: "#0d9488" }}>{formatCurrency((p.priceWithoutDiscount ?? p.productPrice ?? 0) * (p.contractualQuantity ?? 0))}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Barra de ação em massa (bottom) ─── */}
      {selectedCount > 0 && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", padding: 16, pointerEvents: "none", zIndex: 30, animation: "fadeIn .15s ease" }}>
          <div style={{ pointerEvents: "auto", background: "#0f172a", color: "#fff", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 12px 32px rgba(0,0,0,.25)" }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, padding: "0 4px" }}>{selectedCount} selecionado(s)</span>
            <div style={{ width: 1, height: 22, background: "rgba(255,255,255,.15)" }} />
            <button onClick={openEditDrawer} style={{ background: "rgba(255,255,255,.08)", color: "#fff", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Edit3 size={13} /> Editar
            </button>
            <button onClick={handleBulkRemove} style={{ background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 8, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Trash2 size={13} /> Remover
            </button>
            <div style={{ width: 1, height: 22, background: "rgba(255,255,255,.15)" }} />
            <button onClick={() => setSelected({})} style={{ background: "none", color: "rgba(255,255,255,.6)", border: "none", fontSize: 13, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ─── Drawer: Adicionar Produtos ─── */}
      {showAddDrawer && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 40, animation: "fadeIn .15s ease" }} onClick={() => setShowAddDrawer(false)} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(1040px,96vw)", background: "#f7f8fa", zIndex: 41, boxShadow: "-8px 0 32px rgba(0,0,0,.18)", display: "flex", flexDirection: "column", animation: "modalIn .18s ease" }}>

            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 26px", borderBottom: "1px solid #e5e7eb", background: "#fff", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Adicionar Produtos</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{client.name} · {client.code}</div>
              </div>
              <button onClick={() => setShowAddDrawer(false)} style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 16, color: "#64748b" }}>✕</button>
            </div>

            <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 380px", minHeight: 0 }}>

              {/* Coluna esquerda: Catálogo */}
              <div style={{ overflowY: "auto", borderRight: "1px solid #e5e7eb" }}>
                <div style={{ position: "sticky", top: 0, background: "#f7f8fa", padding: "20px 26px 12px", zIndex: 1 }}>
                  {/* Busca */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "6px 6px 6px 14px", marginBottom: 12 }}>
                    <Search size={16} color="#94a3b8" />
                    <input type="text" placeholder="Buscar por nome, SKU, código do produto, segmento ou série..." value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} style={{ flex: 1, border: "none", fontSize: 13.5, padding: "9px 0", background: "transparent", color: "#1f2933", outline: "none" }} />
                  </div>

                  {/* Filtros: série e marca */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <select value={catalogSerie} onChange={e => setCatalogSerie(e.target.value)} style={{ flex: 1, padding: "8px 9px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12.5, color: "#1f2933", background: "#fff" }}>
                      <option value="">Todas as séries</option>
                      {catalogSerieOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <select value={catalogMarca} onChange={e => setCatalogMarca(e.target.value)} style={{ flex: 1, padding: "8px 9px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12.5, color: "#1f2933", background: "#fff" }}>
                      <option value="">Todas as marcas</option>
                      {catalogMarcaOptions.map(opt => <option key={opt} value={opt}>{getMarca(opt)}</option>)}
                    </select>
                  </div>

                  {/* Chips tipo/uso */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {["Material", "Ebook", "Serviço"].map(tipo => (
                      <button key={tipo} onClick={() => setCatalogChipTipo(prev => prev === tipo ? "" : tipo)} style={{ border: "none", background: catalogChipTipo === tipo ? "#0d9488" : "#f1f5f9", color: catalogChipTipo === tipo ? "#fff" : "#475569", fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: "5px 11px", cursor: "pointer" }}>{tipo}</button>
                    ))}
                    <div style={{ width: 1, height: 16, background: "#e2e8f0", margin: "0 2px" }} />
                    {["Aluno", "Professor"].map(uso => (
                      <button key={uso} onClick={() => setCatalogChipUso(prev => prev === uso ? "" : uso)} style={{ border: "none", background: catalogChipUso === uso ? "#0d9488" : "#f1f5f9", color: catalogChipUso === uso ? "#fff" : "#475569", fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: "5px 11px", cursor: "pointer" }}>{uso}</button>
                    ))}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 12.5, color: "#64748b" }}>{filteredCatalog.length} produtos disponíveis</span>
                    <button onClick={stageAllFiltered} style={{ border: "none", background: "none", color: "#0d9488", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>Selecionar todos os filtrados</button>
                  </div>
                </div>

                {/* Lista do catálogo */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 26px 20px" }}>
                  {!drawerReady || loadingCatalog ? (
                    <CatalogSkeleton />
                  ) : (
                    <>
                      {filteredCatalog.slice(0, catalogLimit).map((c: any) => {
                        const isStaged = !!staged[c.id];
                        const isRelated = isAutomaticallyRelatedProduct(c.id);
                        const name = c.descricao_produto || c.descricaoProduto || "";
                        return (
                          <div key={c.id} onClick={() => toggleStage(c.id)} style={{ background: isStaged ? "#f0fdfa" : "#fff", border: `1px solid ${isStaged ? "#0d9488" : "#e5e7eb"}`, borderRadius: 10, padding: "13px 15px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, cursor: "pointer", transition: "all 0.15s" }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                                <span style={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>{name}</span>
                              </div>
                              {c.sku && (
                                <div style={{ fontSize: 11, color: "#64748b", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 9px", display: "inline-block", marginBottom: 6 }}>SKU: {c.sku}</div>
                              )}
                              {((c as any).codigoProduto || (c as any).codigo_produto) && (
                                <div style={{ fontSize: 11, color: "#64748b", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 9px", display: "inline-block", marginBottom: 6, marginLeft: 6 }}>Código: {(c as any).codigoProduto || (c as any).codigo_produto}</div>
                              )}
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                                {c.utilizacao && (
                                  <span style={{ fontSize: 10.5, fontWeight: 600, color: "#334155", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 20, padding: "2px 9px" }}>{c.utilizacao}</span>
                                )}
                                {c.segmento && (
                                  <span style={{ fontSize: 10.5, fontWeight: 600, color: "#fff", background: "#0d9488", borderRadius: 20, padding: "2px 9px" }}>{c.segmento}</span>
                                )}
                                {c.serie && (
                                  <span style={{ fontSize: 10.5, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", borderRadius: 20, padding: "2px 9px" }}>{mapEtapaToGradeName(c.serie)}</span>
                                )}
                              </div>
                              <div style={{ fontSize: 12, color: "#1e293b" }}>Valor Unit.: <strong>{formatCurrency(c.preco || 0)}</strong></div>
                            </div>
                            {isStaged ? (
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#0d9488", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "7px 12px", whiteSpace: "nowrap", flexShrink: 0 }}>
                                {isRelated ? "Relacionado" : "✓ Selecionado"}
                              </span>
                            ) : (
                              <span style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>+ Selecionar</span>
                            )}
                          </div>
                        );
                      })}
                      {filteredCatalog.length > catalogLimit && (
                        <button
                          onClick={() => setCatalogLimit(prev => prev + CATALOG_PAGE_SIZE)}
                          style={{
                            width: "100%",
                            padding: "12px",
                            background: "#f8fafc",
                            border: "1px dashed #cbd5e1",
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#0d9488",
                            cursor: "pointer",
                            marginTop: 4,
                          }}
                        >
                          Ver mais {filteredCatalog.length - catalogLimit} produtos
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Coluna direita: Produtos selecionados */}
              <div style={{ overflowY: "auto", background: "#fff", display: "flex", flexDirection: "column" }}>
                <div style={{ background: "#fff", padding: "20px 22px 0", zIndex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>Produtos selecionados ({stagedRows.length})</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>Informe valor, quantidade, desconto e comentário para cada item.</div>

                  {/* Edição em massa */}
                  {stagedRows.length > 0 && (
                    <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f766e", marginBottom: 2 }}>Edição em massa</div>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Preencha só o(s) campo(s) que quer replicar — os vazios não alteram os itens já configurados.</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Valor (R$) *</label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12.5, color: "#64748b", pointerEvents: "none" }}>R$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="0,00"
                              value={bulkValor}
                              onChange={e => setBulkValor(formatCurrencyMask(e.target.value))}
                              style={{ width: "100%", padding: "7px 9px 7px 30px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5, background: "#fff", boxSizing: "border-box" }}
                            />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Quantidade (und)</label>
                          <input type="number" min={0} step={1} placeholder="1" value={bulkQty} onKeyDown={blockNonQuantityKeys} onChange={e => setBulkQty(e.target.value)} style={{ width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5, background: "#fff" }} />
                        </div>
                        {/* <div>
                          <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Desconto %</label>
                          <input type="number" min={0} max={100} placeholder="0" value={bulkDesconto} onChange={e => setBulkDesconto(e.target.value)} style={{ width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5, background: "#fff" }} />
                        </div> */}
                        <OptionalProductCheckbox
                          id="produto-opcional-massa-adicao"
                          checked={bulkOpcional === true}
                          indeterminate={bulkOpcional === null}
                          onChange={setBulkOpcional}
                          style={{ gridColumn: "span 2" }}
                        />
                        {stagedRows.some(row => canAddShipmentExemption(row.name)) && (
                          <ShipmentExemptCheckbox
                            id="sem-remessa-massa-adicao"
                            checked={bulkSemRemessa === true}
                            indeterminate={bulkSemRemessa === null}
                            onChange={setBulkSemRemessa}
                            style={{ gridColumn: "span 2" }}
                          />
                        )}
                        <div style={{ gridColumn: "span 2" }}>
                          <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Comentário</label>
                          <textarea
                            maxLength={250}
                            placeholder="Mínimo 5 caracteres"
                            value={bulkComentario}
                            onChange={e => setBulkComentario(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "7px 9px",
                              border: `1px solid ${bulkComentario.length > 0 && bulkComentario.trim().length < 5 ? "#fca5a5" : bulkComentario.trim().length >= 5 ? "#86efac" : "#cbd5e1"}`,
                              borderRadius: 7,
                              fontSize: 12.5,
                              background: "#fff",
                              resize: "vertical",
                              minHeight: 38,
                              fontFamily: "inherit",
                              outline: "none",
                            }}
                          />
                          {bulkComentario.length > 0 && bulkComentario.trim().length < 5 && (
                            <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>Mínimo 5 caracteres ({bulkComentario.trim().length}/5)</div>
                          )}
                        </div>
                      </div>
                      <button onClick={applyBulkAll} style={{ width: "100%", background: "#0d9488", color: "#fff", border: "none", borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Aplicar a todos selecionados ({stagedRows.length})</button>
                    </div>
                  )}
                </div>

                {/* Lista staged */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, padding: "0 22px 8px" }}>
                  {stagedRows.map(s => {
                    const isRelated = isAutomaticallyRelatedProduct(s.id);
                    return (
                      <div key={s.id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "13px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{s.name}</span>
                            {isRelated && <span style={{ fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#ccfbf1", borderRadius: 20, padding: "2px 7px" }}>Relacionado</span>}
                          </div>
                          <button
                            onClick={() => unstageProduct(s.id)}
                            title="Remover"
                            style={{ border: "none", background: "none", color: "#94a3b8", fontSize: 15, cursor: "pointer", lineHeight: 1 }}
                          >✕</button>
                        </div>
                        {s.sku && <div style={{ fontSize: 10.5, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 8px", display: "inline-block", marginBottom: 6 }}>SKU: {s.sku}</div>}
                        {s.productCode && <div style={{ fontSize: 10.5, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 20, padding: "2px 8px", display: "inline-block", marginBottom: 6, marginLeft: 6 }}>Código: {s.productCode}</div>}
                        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                          {s.useType && <span style={{ fontSize: 10, fontWeight: 600, color: "#334155", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 20, padding: "2px 8px" }}>{s.useType}</span>}
                          {s.segmento && <span style={{ fontSize: 10, fontWeight: 600, color: "#fff", background: "#0d9488", borderRadius: 20, padding: "2px 8px" }}>{s.segmento}</span>}
                          {s.serie && <span style={{ fontSize: 10, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", borderRadius: 20, padding: "2px 8px" }}>{s.serie}</span>}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                          <div>
                            <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Valor (R$) *</label>
                            <div style={{ position: "relative" }}>
                              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#64748b", pointerEvents: "none" }}>R$</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                placeholder="0,00"
                                value={s.valor}
                                onChange={e => handleValorChange(s.id, e.target.value)}
                                style={{ width: "100%", padding: "8px 9px 8px 32px", border: `1px solid ${s.missingValor ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
                              />
                            </div>
                          </div>
                          <div>
                            <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Quantidade (und)</label>
                            <input type="number" min={0} step={1} placeholder="1" value={s.qtd} onKeyDown={blockNonQuantityKeys} onChange={e => updateStagedField(s.id, "qtd", e.target.value)} style={{ width: "100%", padding: "8px 9px", border: `1px solid ${s.missingQtd ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13 }} />
                            {s.missingQtd && (
                              <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{s.qtdError}</div>
                            )}
                          </div>
                        </div>
                        {/* <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Desconto % (opcional)</label>
                        <input type="number" min={0} max={100} placeholder="0" value={s.desconto} onChange={e => updateStagedField(s.id, "desconto", e.target.value)} style={{ width: "100%", padding: "8px 9px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13 }} />
                      </div> */}
                        <OptionalProductCheckbox
                          id={`produto-opcional-adicao-${s.id}`}
                          checked={s.opcional}
                          onChange={checked => setStaged(prev => ({ ...prev, [s.id]: { ...prev[s.id], opcional: checked } }))}
                          style={{ marginBottom: 10 }}
                        />
                        {canAddShipmentExemption(s.name) && (
                          <ShipmentExemptCheckbox
                            id={`sem-remessa-adicao-${s.id}`}
                            checked={s.semRemessa}
                            onChange={checked => setStaged(prev => ({ ...prev, [s.id]: { ...prev[s.id], semRemessa: checked } }))}
                            style={{ marginBottom: 10 }}
                          />
                        )}
                        <div style={{ marginBottom: 4 }}>
                          <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>
                            Comentário <span style={{ color: "#dc2626" }}>*</span>
                          </label>
                          <textarea
                            maxLength={250}
                            placeholder="Mínimo 5 caracteres"
                            value={s.comentario}
                            onChange={e => updateStagedField(s.id, "comentario", e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 9px",
                              border: `1px solid ${s.missingComentario && s.comentario.length > 0 ? "#fca5a5" : s.comentario.trim().length >= 5 ? "#86efac" : "#e2e8f0"}`,
                              borderRadius: 7,
                              fontSize: 12.5,
                              resize: "vertical",
                              minHeight: 44,
                              fontFamily: "inherit",
                              outline: "none",
                            }}
                          />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          {s.missingComentario && s.comentario.length > 0 ? (
                            <span style={{ fontSize: 10.5, color: "#dc2626" }}>Mínimo 5 caracteres ({s.comentario.trim().length}/5)</span>
                          ) : s.missingComentario ? (
                            <span style={{ fontSize: 10.5, color: "#f97316" }}>Comentário obrigatório</span>
                          ) : (
                            <span style={{ fontSize: 10.5, color: "#16a34a" }}>✓ Comentário válido</span>
                          )}
                          <span style={{ fontSize: 10.5, color: "#94a3b8" }}>{s.charsRemaining} restantes</span>
                        </div>
                        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "9px 11px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 12, color: "#475569" }}>Total com desc.:</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#0d9488" }}>{s.totalStr}</span>
                        </div>
                        {s.missingValor && (
                          <div style={{ fontSize: 11, color: "#dc2626", marginTop: 6 }}>
                            Valor não pode ser negativo.
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {stagedRows.length === 0 && (
                    <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "36px 12px", border: "1px dashed #e2e8f0", borderRadius: 10 }}>
                      Selecione produtos na lista à esquerda para configurá-los aqui.
                    </div>
                  )}
                </div>

                {/* Total estimado */}
                {stagedRows.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, padding: "14px 22px 0", borderTop: "1px dashed #e2e8f0", fontSize: 13 }}>
                    <span style={{ color: "#64748b" }}>Total estimado da adição</span>
                    <span style={{ fontWeight: 700, color: "#0d9488", fontSize: 15 }}>{formatCurrency(stagedGrandTotal)}</span>
                  </div>
                )}

                {/* Botões do drawer */}
                <div style={{ position: "sticky", bottom: 0, background: "#fff", display: "flex", gap: 10, marginTop: 16, padding: "16px 22px", borderTop: "1px solid #e5e7eb" }}>
                  <button onClick={() => setShowAddDrawer(false)} style={{ flex: 1, background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 9, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
                  <button onClick={handleConfirmAddProducts} disabled={addDisabled || actionLoading} style={{ flex: 1, background: addDisabled ? "#94a3b8" : "#0d9488", color: "#fff", border: "none", borderRadius: 9, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: addDisabled ? "not-allowed" : "pointer" }}>
                    {actionLoading ? "Adicionando..." : `Adicionar ${stagedRows.length} produto(s)`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Drawer: Editar Produtos ─── */}
      {showEditDrawer && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 40, animation: "fadeIn .15s ease" }} onClick={() => setShowEditDrawer(false)} />
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(560px,96vw)", background: "#fff", zIndex: 41, boxShadow: "-8px 0 32px rgba(0,0,0,.18)", display: "flex", flexDirection: "column", animation: "modalIn .18s ease" }}>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 26px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Editar Produtos Selecionados</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{Object.keys(editStaged).length} produto(s) selecionado(s)</div>
              </div>
              <button onClick={() => setShowEditDrawer(false)} style={{ width: 34, height: 34, borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 16, color: "#64748b" }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              <div style={{ padding: "20px 26px 0" }}>
                {/* Edição em massa */}
                {Object.keys(editStaged).length > 0 && (
                  <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#0f766e", marginBottom: 2 }}>Edição em massa</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Preencha só o(s) campo(s) que quer replicar — os vazios não alteram os itens já configurados.</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                      <div>
                        <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Valor (R$) *</label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12.5, color: "#64748b", pointerEvents: "none" }}>R$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0,00"
                            value={editBulkValor}
                            onChange={e => setEditBulkValor(formatCurrencyMask(e.target.value))}
                            style={{ width: "100%", padding: "7px 9px 7px 30px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5, background: "#fff", boxSizing: "border-box" }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Quantidade (und)</label>
                        <input type="number" min={0} step={1} placeholder="1" value={editBulkQty} onKeyDown={blockNonQuantityKeys} onChange={e => setEditBulkQty(e.target.value)} style={{ width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5 }} />
                      </div>
                      {/* <div>
                        <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Desconto % (opcional)</label>
                        <input type="number" min={0} max={100} placeholder="0" value={editBulkDesconto} onChange={e => setEditBulkDesconto(e.target.value)} style={{ width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5 }} />
                      </div> */}
                      <OptionalProductCheckbox
                        id="produto-opcional-massa-edicao"
                        checked={editBulkOpcional === true}
                        indeterminate={editBulkOpcional === null}
                        onChange={setEditBulkOpcional}
                        style={{ gridColumn: "span 2" }}
                      />
                      {hasEditableShipmentExemption && (
                        <ShipmentExemptCheckbox
                          id="sem-remessa-massa-edicao"
                          checked={editBulkSemRemessa === true}
                          indeterminate={editBulkSemRemessa === null}
                          onChange={setEditBulkSemRemessa}
                          style={{ gridColumn: "span 2" }}
                        />
                      )}
                      <div style={{ gridColumn: "span 2" }}>
                        <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3 }}>Comentário</label>
                        <input type="text" placeholder="Opcional" value={editBulkComentario} onChange={e => setEditBulkComentario(e.target.value)} style={{ width: "100%", padding: "7px 9px", border: "1px solid #cbd5e1", borderRadius: 7, fontSize: 12.5 }} />
                      </div>
                    </div>
                    <button onClick={applyEditBulkAll} style={{ width: "100%", background: "#0d9488", color: "#fff", border: "none", borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Aplicar a todos selecionados ({Object.keys(editStaged).length})</button>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 26px 20px" }}>
                {Object.entries(editStaged).map(([idStr, data]) => {
                  const id = Number(idStr);
                  const p = activeProducts.find(p => p.profileClientId === id);
                  const valorNum = parseCurrencyInput(data.valor);
                  const qtdNum = Number(data.qtd) || 0;
                  const qtdError = getQuantityError(data.qtd);
                  const descNum = Number(data.desconto) || 0;
                  const totalEdit = valorNum * qtdNum;
                  const comentarioLen = (data.comentario || "").trim().length;
                  const comentarioInvalid = comentarioLen > 0 && comentarioLen < 5;
                  return (
                    <div key={id} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "13px 14px" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 10 }}>{p?.productDescription || ""}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Valor (R$) *</label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#64748b", pointerEvents: "none" }}>R$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="0,00"
                              value={data.valor}
                              onChange={e => {
                                const formatted = formatCurrencyMask(e.target.value);
                                setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], valor: formatted } }));
                              }}
                              style={{ width: "100%", padding: "8px 9px 8px 32px", border: `1px solid ${valorNum < 0 ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13, boxSizing: "border-box" }}
                            />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Quantidade (und)</label>
                          <input type="number" min={0} step={1} placeholder="1" value={data.qtd} onKeyDown={blockNonQuantityKeys} onChange={e => setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], qtd: e.target.value } }))} style={{ width: "100%", padding: "8px 9px", border: `1px solid ${qtdError ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13 }} />
                          {qtdError && (
                            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>{qtdError}</div>
                          )}
                        </div>
                      </div>
                      {/* <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Desconto % (opcional)</label>
                        <input type="number" min={0} max={100} placeholder="0" value={data.desconto} onChange={e => setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], desconto: e.target.value } }))} style={{ width: "100%", padding: "8px 9px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13 }} />
                      </div> */}
                      <OptionalProductCheckbox
                        id={`produto-opcional-edicao-${id}`}
                        checked={!!data.opcional}
                        onChange={checked => setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], opcional: checked } }))}
                        style={{ marginBottom: 10 }}
                      />
                      {canEditShipmentExemption(p) && (
                        <ShipmentExemptCheckbox
                          id={`sem-remessa-edicao-${id}`}
                          checked={!!data.semRemessa}
                          onChange={checked => setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], semRemessa: checked } }))}
                          style={{ marginBottom: 10 }}
                        />
                      )}
                      <div>
                        <label style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>Comentário <span style={{ color: "#dc2626" }}>*</span></label>
                        <textarea
                          placeholder="Observações sobre esta alteração (mín. 5 caracteres)..."
                          value={data.comentario}
                          onChange={e => setEditStaged(prev => ({ ...prev, [id]: { ...prev[id], comentario: e.target.value } }))}
                          style={{
                            width: "100%",
                            padding: "8px 9px",
                            border: `1px solid ${comentarioInvalid ? "#fca5a5" : comentarioLen >= 5 ? "#86efac" : "#e2e8f0"}`,
                            borderRadius: 7,
                            fontSize: 12.5,
                            resize: "vertical",
                            minHeight: 44,
                            fontFamily: "inherit",
                          }}
                        />
                        {comentarioInvalid && (
                          <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 4 }}>Mínimo 5 caracteres ({comentarioLen}/5)</div>
                        )}
                      </div>
                      <div style={{ background: "#f8fafc", borderRadius: 8, padding: "9px 11px", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                        <span style={{ fontSize: 12, color: "#475569" }}>Total com desc.:</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0d9488" }}>{formatCurrency(totalEdit)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, padding: "16px 26px", borderTop: "1px solid #e5e7eb", flexShrink: 0 }}>
              <button onClick={() => setShowEditDrawer(false)} style={{ flex: 1, background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 9, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
              <button onClick={handleSaveEdits} disabled={actionLoading || editSaveDisabled} style={{ flex: 1, background: actionLoading || editSaveDisabled ? "#94a3b8" : "#0d9488", color: "#fff", border: "none", borderRadius: 9, padding: 11, fontSize: 13.5, fontWeight: 600, cursor: actionLoading || editSaveDisabled ? "not-allowed" : "pointer" }}>
                {actionLoading ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── Modal: Confirmar Inativação ─── */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, animation: "fadeIn .15s ease" }}>
          <div style={{ background: "#fff", borderRadius: 14, width: 440, maxWidth: "90vw", padding: 26, animation: "modalIn .18s ease" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AlertTriangle size={20} color="#dc2626" />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>Inativar {confirmCountLabel}?</div>
                <div style={{ fontSize: 13.5, color: "#64748b", marginTop: 6, lineHeight: 1.5 }}>
                  Ao confirmar, o(s) produto(s) deixará(ão) de aparecer para o cliente. O histórico de faturamento é mantido e a ação pode ser revertida depois em "Histórico".
                </div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Comentário (obrigatório) *</label>
              <textarea
                placeholder="Descreva o motivo da inativação..."
                value={confirmComentario}
                onChange={e => setConfirmComentario(e.target.value)}
                style={{ width: "100%", padding: "9px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", minHeight: 70 }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <button onClick={() => { setShowConfirm(false); setConfirmMode(null); }} style={{ background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={handleConfirmInactivate} disabled={!confirmComentario.trim() || actionLoading} style={{ background: !confirmComentario.trim() ? "#94a3b8" : "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13.5, fontWeight: 600, cursor: !confirmComentario.trim() ? "not-allowed" : "pointer" }}>
                {actionLoading ? "Processando..." : "Confirmar Inativação"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalIn { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        ::selection { background: rgba(13,148,136,.2); }
        input:focus, select:focus, textarea:focus { outline: none; border-color: #0d9488 !important; box-shadow: 0 0 0 3px rgba(13,148,136,.12); }
      `}</style>
    </div>
  );
}
